export type ColorScheme = "light" | "dark";

/** Identité couleur CodeFirst (thème legacy `Theme.ts`) — référence produit, hors palette SaaS sombre. */
export const codeFirstBrand = {
  jungleGreen: "#021D1E",
  richBlack: "#023C40",
  stealTeal: "#62929E",
  aquamarine: "#3DDC97",
  ghostWhite: "#F0EFF4",
  info: "#4466FF",
} as const;

/** Mode sombre : canvas quasi noir, panneaux un ton au-dessus (pas de blanc en alpha). */
const darkCanvas = "#0d1117";
const darkPanel = "#161b22";
const darkElevated = "#21262d";

/** Accents portail (même teintes qu’en `dark` ci-dessous) — hex pour styles hors `useTheme`. */
export const saasPortalAccents = {
  blue: "#58a6ff",
  purple: "#a371f7",
} as const;

export type SaasPalette = {
  scheme: ColorScheme;
  pageBg: string;
  surface: string;
  surfaceHover: string;
  border: string;
  borderStrong: string;
  titleGradient: string;
  textPrimary: string;
  textMuted: string;
  topBarBg: string;
  topBarBorder: string;
  topBarText: string;
  topBarTextMuted: string;
  topBarIconBorder: string;
  topBarIconColor: string;
  topBarSearchBg: string;
  topBarSearchBorder: string;
  topBarSearchHoverBg: string;
  topBarSearchText: string;
  topBarSearchHint: string;
  topBarClusterFade: string;
  topBarLogoButtonBg: string;
  dialogBg: string;
  dialogBorder: string;
  dialogHeaderBg: string;
  inputPlaceholder: string;
  listSubheader: string;
  listItemTitle: string;
  listItemSubtitle: string;
  listRowHover: string;
  listRowSelected: string;
  divider: string;
  chipOnSurface: string;
  accentBlue: string;
  accentPurple: string;
  /** Dégradé des CTA (pastel en clair pour supporter un texte foncé). */
  primaryActionGradient: string;
  /** Texte sur CTA : jamais #fff en mode clair (lisible sur pastel / dégradé). */
  textOnPrimaryAction: string;
  /** Texte / icône très atténué (désactivé, empty state). */
  disabledFg: string;
  /** Fond discret pour contrôle désactivé ou zone technique. */
  disabledBg: string;
  /** Ligne ou carte imbriquée, fond de piste fine. */
  nestedShade: string;
  nestedShadeHover: string;
  /** Pistes de slider / scrollbar custom. */
  trackRail: string;
  trackRailStrong: string;
  /** Markdown : code inline. */
  codeInlineBg: string;
  /** Markdown : bloc pre. */
  codeBlockBg: string;
  homeHeadingShadow: string;
  quickAccessIconFilter: string;
};

/** Dark : fond #0d1117 (noir profond), cartes #161b22, accents bleu / violet. */
const dark: Omit<SaasPalette, "scheme"> = {
  pageBg: darkCanvas,
  surface: darkPanel,
  surfaceHover: darkElevated,
  border: "rgba(240, 246, 252, 0.1)",
  borderStrong: "rgba(88, 166, 255, 0.35)",
  titleGradient: "linear-gradient(135deg, #58a6ff 0%, #a371f7 100%)",
  textPrimary: "#f0f6fc",
  textMuted: "rgba(240, 246, 252, 0.65)",
  topBarBg: "rgba(13, 17, 23, 0.96)",
  topBarBorder: "rgba(240, 246, 252, 0.1)",
  topBarText: "#f0f6fc",
  topBarTextMuted: "rgba(139, 148, 158, 0.95)",
  topBarIconBorder: "rgba(240, 246, 252, 0.12)",
  topBarIconColor: "#c9d1d9",
  topBarSearchBg: darkPanel,
  topBarSearchBorder: "rgba(240, 246, 252, 0.12)",
  topBarSearchHoverBg: darkElevated,
  topBarSearchText: "rgba(139, 148, 158, 0.95)",
  topBarSearchHint: "rgba(139, 148, 158, 0.65)",
  topBarClusterFade: darkCanvas,
  topBarLogoButtonBg: "rgba(240, 246, 252, 0.06)",
  dialogBg: darkElevated,
  dialogBorder: "rgba(88, 166, 255, 0.22)",
  dialogHeaderBg: darkPanel,
  inputPlaceholder: "rgba(139, 148, 158, 0.75)",
  listSubheader: "rgba(139, 148, 158, 0.9)",
  listItemTitle: "#f0f6fc",
  listItemSubtitle: "rgba(139, 148, 158, 0.88)",
  listRowHover: "rgba(88, 166, 255, 0.08)",
  listRowSelected: "rgba(88, 166, 255, 0.14)",
  divider: "rgba(240, 246, 252, 0.08)",
  chipOnSurface: "#1c2128",
  accentBlue: "#58a6ff",
  accentPurple: "#a371f7",
  primaryActionGradient: "linear-gradient(135deg, #58a6ff 0%, #a371f7 100%)",
  textOnPrimaryAction: "#e8edf4",
  disabledFg: "rgba(240, 246, 252, 0.34)",
  disabledBg: "rgba(240, 246, 252, 0.06)",
  nestedShade: "rgba(240, 246, 252, 0.05)",
  nestedShadeHover: "rgba(240, 246, 252, 0.1)",
  trackRail: "rgba(240, 246, 252, 0.1)",
  trackRailStrong: "rgba(240, 246, 252, 0.32)",
  codeInlineBg: "rgba(240, 246, 252, 0.1)",
  codeBlockBg: "rgba(0, 0, 0, 0.32)",
  homeHeadingShadow: "0 2px 8px rgba(0, 0, 0, 0.45)",
  quickAccessIconFilter: "brightness(0) invert(1)",
};

/** Light : surfaces un peu plus soutenues (moins #eef1f6 « lave »), texte toujours foncé. */
const light: Omit<SaasPalette, "scheme"> = {
  pageBg: "#d6dce6",
  surface: "#dfe5ee",
  surfaceHover: "#d4dbe6",
  border: "rgba(15, 23, 42, 0.11)",
  borderStrong: "rgba(21, 101, 192, 0.32)",
  titleGradient: "linear-gradient(135deg, #0c4a6e 0%, #5b21a6 100%)",
  textPrimary: "#0f172a",
  textMuted: "#3d4f66",
  topBarBg: "rgba(223, 229, 238, 0.94)",
  topBarBorder: "rgba(15, 23, 42, 0.1)",
  topBarText: "#0f172a",
  topBarTextMuted: "#3d4f66",
  topBarIconBorder: "rgba(21, 101, 192, 0.22)",
  topBarIconColor: "#0c4a6e",
  topBarSearchBg: "#d8dfe9",
  topBarSearchBorder: "rgba(15, 23, 42, 0.12)",
  topBarSearchHoverBg: "#cfd8e6",
  topBarSearchText: "#1e293b",
  topBarSearchHint: "#475569",
  topBarClusterFade: "rgba(223, 229, 238, 0.96)",
  topBarLogoButtonBg: "rgba(21, 101, 192, 0.1)",
  dialogBg: "#dfe5ee",
  dialogBorder: "rgba(21, 101, 192, 0.22)",
  dialogHeaderBg: "rgba(215, 222, 232, 0.98)",
  inputPlaceholder: "#475569",
  listSubheader: "#334155",
  listItemTitle: "#0f172a",
  listItemSubtitle: "#3d4f66",
  listRowHover: "rgba(21, 101, 192, 0.09)",
  listRowSelected: "rgba(21, 101, 192, 0.14)",
  divider: "rgba(15, 23, 42, 0.12)",
  chipOnSurface: "rgba(21, 101, 192, 0.08)",
  accentBlue: "#1565c0",
  accentPurple: "#7b1fa2",
  primaryActionGradient: "linear-gradient(135deg, #cfe3fc 0%, #e4daf8 100%)",
  textOnPrimaryAction: "#0f172a",
  disabledFg: "rgba(15, 23, 42, 0.36)",
  disabledBg: "rgba(15, 23, 42, 0.05)",
  nestedShade: "rgba(15, 23, 42, 0.04)",
  nestedShadeHover: "rgba(21, 101, 192, 0.08)",
  trackRail: "rgba(15, 23, 42, 0.1)",
  trackRailStrong: "rgba(15, 23, 42, 0.22)",
  codeInlineBg: "rgba(15, 23, 42, 0.07)",
  codeBlockBg: "rgba(15, 23, 42, 0.06)",
  homeHeadingShadow: "none",
  quickAccessIconFilter: "brightness(0) invert(0.18)",
};

export function getSaasPalette(scheme: ColorScheme): SaasPalette {
  return scheme === "light" ? { scheme: "light", ...light } : { scheme: "dark", ...dark };
}
